import { useState, useRef, useEffect, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Camera, RotateCcw, Check, AlertCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// KioskSelfieCapture
// ---------------------------------------------------------------------------
// Camera capture screen for kiosk clock-in selfie verification.
//
// Props:
//   onSelfieComplete (function) - called with base64 JPEG string (or null)
//   goBack           (function) - navigate back to confirm screen
// ---------------------------------------------------------------------------

export default function KioskSelfieCapture({ selfieComplete, goBack }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [captured, setCaptured] = useState(null); // base64 string
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  // -----------------------------------------------------------------------
  // Start camera on mount
  // -----------------------------------------------------------------------
  const startCamera = useCallback(async () => {
    setCameraError(null);
    setCameraReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setCameraError(
        err.name === 'NotAllowedError'
          ? 'Camera access was denied. Please allow camera permissions.'
          : 'Could not access camera. It may be in use or unavailable.',
      );
    }
  }, []);

  // Stop camera tracks
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  // -----------------------------------------------------------------------
  // Capture photo from video stream
  // -----------------------------------------------------------------------
  const handleCapture = useCallback(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');

    // Mirror the image to match the preview
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const base64 = canvas.toDataURL('image/jpeg', 0.6);
    setCaptured(base64);
    stopCamera();
  }, [stopCamera]);

  // -----------------------------------------------------------------------
  // Retake — restart camera
  // -----------------------------------------------------------------------
  const handleRetake = useCallback(() => {
    setCaptured(null);
    startCamera();
  }, [startCamera]);

  // -----------------------------------------------------------------------
  // Use photo — pass to parent
  // -----------------------------------------------------------------------
  const handleUsePhoto = useCallback(() => {
    selfieComplete(captured);
  }, [selfieComplete, captured]);

  // -----------------------------------------------------------------------
  // Continue without photo (camera error fallback)
  // -----------------------------------------------------------------------
  const handleSkip = useCallback(() => {
    selfieComplete(null);
  }, [selfieComplete]);

  // -----------------------------------------------------------------------
  // Render: camera error state
  // -----------------------------------------------------------------------
  if (cameraError) {
    return (
      <motion.div
        className="flex-1 flex flex-col items-center justify-center px-6 gap-6"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
          <AlertCircle className="w-10 h-10 text-amber-600" />
        </div>
        <div className="text-center max-w-sm">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Camera Unavailable
          </h2>
          <p className="text-lg text-slate-500">{cameraError}</p>
        </div>
        <div className="flex gap-4 mt-4">
          <motion.button
            type="button"
            className="h-16 px-10 rounded-xl text-xl font-semibold border-2 border-slate-300 text-slate-600 bg-white hover:bg-slate-50 active:bg-slate-100 transition-colors shadow-sm"
            whileTap={{ scale: 0.95 }}
            onClick={goBack}
          >
            Go Back
          </motion.button>
          <motion.button
            type="button"
            className="h-16 px-10 rounded-xl text-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-md"
            whileTap={{ scale: 0.95 }}
            onClick={handleSkip}
          >
            Continue Without Photo
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: captured photo review
  // -----------------------------------------------------------------------
  if (captured) {
    return (
      <motion.div
        className="flex-1 flex flex-col items-center justify-center px-6 gap-6"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-2xl font-bold text-slate-800">
          Look good?
        </h2>

        <div className="relative rounded-2xl overflow-hidden shadow-lg ring-4 ring-white">
          <img
            src={captured}
            alt="Selfie preview"
            className="w-[320px] h-[240px] object-cover"
          />
        </div>

        <div className="flex gap-4 mt-2">
          <motion.button
            type="button"
            className="h-16 px-10 rounded-xl text-xl font-semibold border-2 border-slate-300 text-slate-600 bg-white hover:bg-slate-50 active:bg-slate-100 transition-colors shadow-sm flex items-center gap-3"
            whileTap={{ scale: 0.95 }}
            onClick={handleRetake}
          >
            <RotateCcw className="w-6 h-6" />
            Retake
          </motion.button>
          <motion.button
            type="button"
            className="h-16 px-10 rounded-xl text-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition-colors shadow-md flex items-center gap-3"
            whileTap={{ scale: 0.95 }}
            onClick={handleUsePhoto}
          >
            <Check className="w-6 h-6" />
            Use Photo
          </motion.button>
        </div>
      </motion.div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: live camera preview
  // -----------------------------------------------------------------------
  return (
    <motion.div
      className="flex-1 flex flex-col items-center justify-center px-6 gap-6"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-2xl font-bold text-slate-800">
        Take a selfie to verify your identity
      </h2>

      <div className="relative rounded-2xl overflow-hidden shadow-lg ring-4 ring-white bg-slate-900">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-[320px] h-[240px] object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
        {!cameraReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
            <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
      </div>

      <motion.button
        type="button"
        className="h-20 w-20 rounded-full bg-white border-4 border-emerald-500 hover:border-emerald-600 active:border-emerald-700 transition-colors shadow-lg flex items-center justify-center disabled:opacity-40"
        whileTap={{ scale: 0.9 }}
        onClick={handleCapture}
        disabled={!cameraReady}
        aria-label="Take photo"
      >
        <Camera className="w-8 h-8 text-emerald-600" />
      </motion.button>

      <p className="text-base text-slate-400">
        Tap the button to take a photo
      </p>
    </motion.div>
  );
}
